// BusinessAiScanModal — US-114 (inputs), US-115 (persisted/realtime progress),
// and US-116 (review + cherry-pick apply).
//
// The modal walks three phases:
//   1. `input` — collect website + LinkedIn URLs + additional context.
//      Submit calls `useBusinessAiScan` which acks with a `runId`.
//   2. `progress` — once we have a `runId` we render the step list. Realtime
//      updates it while persisted-business polling provides recovery.
//   3. `review` — fetches the persisted `lastScanSuggestion` from the
//      business row and lets the user opt-in to each of the 7 fields. The
//      apply button calls `useApplyBusinessScanSuggestions` which patches
//      the business row server-side.

import { useEffect, useMemo, useRef, useState } from 'react';

import {
  useApplyBusinessScanSuggestions,
  useBusiness,
  useBusinessAiScan,
  useUserHubChannel,
  type Business,
  type BusinessScanField,
  type BusinessScanSuggestion,
} from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import {
  Banner,
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  FieldAssembly,
  Input,
  Pill,
  Spinner,
  Textarea,
} from '@barel/ui';

import { runMutationWithToast } from '@/lib/mutationToast';

export interface BusinessAiScanModalProps {
  open: boolean;
  onClose: () => void;
  /** Business id we're scanning. */
  businessId: string;
  /** Initial website value (pre-filled from `business.website`). */
  initialWebsite?: string;
  /** Initial company LinkedIn (pre-filled from socials). */
  initialLinkedinCompany?: string;
  /** Initial profile LinkedIn (pre-filled from account URL). */
  initialLinkedinProfile?: string;
  /** Current editor values, including unsaved changes, for an honest diff. */
  currentValues?: Partial<Pick<Business, BusinessScanField>>;
  /** Merge the server-applied fields into any unsaved parent draft. */
  onApplied?: (business: Business, appliedFields: BusinessScanField[]) => void;
}

type Phase = 'input' | 'progress' | 'review-loading' | 'review' | 'error';
type ScanFailureReason = 'scan' | 'timeout';

const SCAN_POLL_INTERVAL_MS = 2_000;
// Three bounded source fetches can consume 30s before the provider's own
// completion window. Keep the client envelope above that valid backend path.
const SCAN_TIMEOUT_MS = 150_000;

interface ScanStep {
  id: 'website' | 'linkedinCompany' | 'linkedinProfile' | 'history' | 'synth';
  active: boolean;
  done: boolean;
}

/** The 7 review fields (US-116). Order is the canonical render order. */
const REVIEW_FIELDS: readonly BusinessScanField[] = [
  'description',
  'personas',
  'pains',
  'valueProps',
  'salesRules',
  'questionGuide',
  'hotSignals',
];

function emptyAcceptedFields(): Record<BusinessScanField, boolean> {
  return {
    description: false,
    personas: false,
    pains: false,
    valueProps: false,
    hotSignals: false,
    questionGuide: false,
    salesRules: false,
  };
}

/** Map the backend `step` strings (`website-fetched`, `linkedin-company-queued`,
 *  `linkedin-profile-queued`, `synthesis-complete`) onto the local `ScanStep.id`. */
function stepIdFromBackendStep(step: string): ScanStep['id'] | null {
  if (step.startsWith('website')) return 'website';
  if (step.startsWith('linkedin-company')) return 'linkedinCompany';
  if (step.startsWith('linkedin-profile')) return 'linkedinProfile';
  if (step.startsWith('history')) return 'history';
  if (step.startsWith('synthesis')) return 'synth';
  return null;
}

/** Truncate-safe preview helper — produces a 1-line summary of a value. */
function previewValue(value: unknown, max = 120): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') {
    return value.length > max ? `${value.slice(0, max)}…` : value;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return '';
    const first = value[0];
    if (typeof first === 'string') {
      return previewValue(value.join(', '), max);
    }
    if (first && typeof first === 'object' && 'title' in first) {
      const titles = (value as { title?: string }[])
        .map((p) => p.title ?? '')
        .filter(Boolean)
        .join(', ');
      return previewValue(titles, max);
    }
    try {
      return previewValue(JSON.stringify(value), max);
    } catch {
      return '';
    }
  }
  try {
    return previewValue(JSON.stringify(value), max);
  } catch {
    return '';
  }
}

/** Returns true iff the field has a meaningful current/suggested value. */
function fieldHasValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

export function BusinessAiScanModal({
  open,
  onClose,
  businessId,
  initialWebsite = '',
  initialLinkedinCompany = '',
  initialLinkedinProfile = '',
  currentValues,
  onApplied,
}: BusinessAiScanModalProps) {
  const t = useT('businesses');
  const tCommon = useT('common');
  const tErrors = useT('errors');
  const scan = useBusinessAiScan();
  const apply = useApplyBusinessScanSuggestions();
  const businessQuery = useBusiness(businessId, { enabled: open });
  const { refetch: refetchBusiness } = businessQuery;

  const [phase, setPhase] = useState<Phase>('input');
  const [website, setWebsite] = useState(initialWebsite);
  const [linkedinCompany, setLinkedinCompany] = useState(initialLinkedinCompany);
  const [linkedinProfile, setLinkedinProfile] = useState(initialLinkedinProfile);
  const [additionalContext, setAdditionalContext] = useState('');
  const [readHistory, setReadHistory] = useState(true);
  // Wave A2 — the runId scopes the realtime listeners. Once `useBusinessAiScan`
  // acks with a runId, we subscribe to `business.scan.progress`/.complete and
  // filter on it so a parallel scan in another tab cannot crosstalk.
  const [runId, setRunId] = useState<string | null>(null);
  const [runStartedAt, setRunStartedAt] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [steps, setSteps] = useState<ScanStep[]>([]);
  const [failureReason, setFailureReason] = useState<ScanFailureReason | null>(null);
  // Wave C2 — review-phase state.
  const [accepted, setAccepted] = useState<Record<BusinessScanField, boolean>>(emptyAcceptedFields);
  const [diffOpen, setDiffOpen] = useState<BusinessScanField | null>(null);
  const previousOpen = useRef({ open: false, businessId });

  const business = businessQuery.data?.business;
  const matchingRun = Boolean(runId && business?.lastScanRunId === runId);
  const suggestion = (
    matchingRun ? business?.lastScanSuggestion : null
  ) as BusinessScanSuggestion | null;

  // Reset runtime state on close and rehydrate source props on every open.
  // The transition guard prevents a parent draft update while the modal is
  // already open from overwriting source edits made inside the dialog.
  useEffect(() => {
    const justOpened =
      open && (!previousOpen.current.open || previousOpen.current.businessId !== businessId);
    previousOpen.current = { open, businessId };

    if (justOpened) {
      setWebsite(initialWebsite);
      setLinkedinCompany(initialLinkedinCompany);
      setLinkedinProfile(initialLinkedinProfile);
      setAdditionalContext('');
      setReadHistory(true);
    }

    if (!open) {
      setPhase('input');
      setRunId(null);
      setRunStartedAt(null);
      setSteps([]);
      setElapsed(0);
      setFailureReason(null);
      setAccepted(emptyAcceptedFields());
      setDiffOpen(null);
    }
  }, [businessId, initialLinkedinCompany, initialLinkedinProfile, initialWebsite, open]);

  // Tick the elapsed counter while the scan is running.
  useEffect(() => {
    if ((phase !== 'progress' && phase !== 'review-loading') || runStartedAt === null) return;
    const updateElapsed = () => {
      setElapsed(Math.max(0, Math.round((Date.now() - runStartedAt) / 1000)));
    };
    updateElapsed();
    const id = window.setInterval(() => {
      updateElapsed();
    }, 1000);
    return () => window.clearInterval(id);
  }, [phase, runStartedAt]);

  // Realtime can be missed while the tab sleeps or the websocket reconnects.
  // Poll the persisted business row as a recovery path until this run's
  // suggestion is visible.
  useEffect(() => {
    const waitingForSuggestion = phase === 'progress' || phase === 'review-loading';
    if (!open || !runId || !waitingForSuggestion) return;
    void refetchBusiness();
    const id = window.setInterval(() => {
      void refetchBusiness();
    }, SCAN_POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [open, phase, refetchBusiness, runId]);

  // A matching persisted result is authoritative even if the completion
  // websocket frame was missed.
  useEffect(() => {
    if (
      (phase !== 'progress' && phase !== 'review-loading') ||
      !matchingRun ||
      suggestion === null
    ) {
      return;
    }
    setSteps((prev) => prev.map((step) => ({ ...step, active: false, done: true })));
    setFailureReason(null);
    setPhase('review');
  }, [matchingRun, phase, suggestion]);

  // Bound the whole run, including the persistence wait after a successful
  // completion event, instead of allowing an infinite spinner.
  useEffect(() => {
    if ((phase !== 'progress' && phase !== 'review-loading') || runStartedAt === null) {
      return;
    }
    const remaining = Math.max(0, SCAN_TIMEOUT_MS - (Date.now() - runStartedAt));
    const id = window.setTimeout(() => {
      setFailureReason('timeout');
      setPhase('error');
    }, remaining);
    return () => window.clearTimeout(id);
  }, [phase, runStartedAt]);

  // Wave A2 — listen for `business.scan.progress` + `business.scan.complete`
  // events emitted by `apps/api/src/workflows/businessScan.ts`. The events
  // carry the `runId`; we ignore frames that don't match our active run so
  // a parallel scan from another tab cannot leak across.
  useUserHubChannel('business.scan.progress', (event) => {
    const payload = (('payload' in event ? event.payload : null) ?? {}) as {
      runId?: string;
      step?: string;
      progressPct?: number;
    };
    if (!runId || payload.runId !== runId || !payload.step) return;
    setSteps((prev) => {
      const id = stepIdFromBackendStep(payload.step ?? '');
      if (!id) return prev;
      const completedIndex = prev.findIndex((step) => step.id === id);
      return prev.map((step, index) => {
        if (index <= completedIndex) return { ...step, active: false, done: true };
        if (index === completedIndex + 1) return { ...step, active: true, done: false };
        return { ...step, active: false };
      });
    });
  });
  useUserHubChannel('business.scan.complete', (event) => {
    const payload = (('payload' in event ? event.payload : null) ?? {}) as {
      runId?: string;
      ok?: boolean;
    };
    if (!runId || payload.runId !== runId) return;
    if (payload.ok === false) {
      setFailureReason('scan');
      setPhase('error');
      return;
    }
    setSteps((prev) => prev.map((s) => ({ ...s, active: false, done: true })));
    setPhase('review-loading');
    // Stay in a loading review state until this run's persisted suggestion
    // is visible; polling above covers missed/early completion events.
    void businessQuery.refetch();
  });

  const hasInput =
    Boolean(website.trim()) ||
    Boolean(linkedinCompany.trim()) ||
    Boolean(linkedinProfile.trim()) ||
    Boolean(additionalContext.trim()) ||
    readHistory;

  const onSubmit = async (): Promise<void> => {
    const sources = {
      website: website.trim() || undefined,
      linkedinCompany: linkedinCompany.trim() || undefined,
      linkedinProfile: linkedinProfile.trim() || undefined,
      additionalContext: additionalContext.trim() || undefined,
      readHistory,
    };
    // Wave M2 — the API now fails loud (5xx) when the scan workflow can't
    // start; surface that as an error toast instead of an unhandled
    // rejection, and stay on the input phase so the user can retry.
    await runMutationWithToast(() => scan.mutateAsync({ id: businessId, input: sources }), {
      error: { title: t('ai.errorToast') },
      onSuccess: (ack) => {
        setRunId(ack.runId);
        setRunStartedAt(Date.now());
        setElapsed(0);
        setFailureReason(null);
        // Seed the step list — only show the steps that will fire.
        const next: ScanStep[] = [];
        if (sources.website) next.push({ id: 'website', active: true, done: false });
        if (sources.linkedinCompany)
          next.push({ id: 'linkedinCompany', active: false, done: false });
        if (sources.linkedinProfile)
          next.push({ id: 'linkedinProfile', active: false, done: false });
        if (sources.readHistory) next.push({ id: 'history', active: false, done: false });
        next.push({ id: 'synth', active: false, done: false });
        setSteps(next);
        setPhase('progress');
      },
      onError: () => {
        setFailureReason('scan');
        setPhase('error');
      },
    });
  };

  // ─── Review-phase derived state ─────────────────────────────────────────
  const acceptedFields = useMemo(() => REVIEW_FIELDS.filter((f) => accepted[f]), [accepted]);
  const reviewableFields = useMemo(
    () => REVIEW_FIELDS.filter((f) => suggestion && fieldHasValue(suggestion[f])),
    [suggestion],
  );

  const onApply = async (): Promise<void> => {
    if (!business || !runId || acceptedFields.length === 0) return;
    await runMutationWithToast(
      async () => {
        const result = await apply.mutateAsync({
          id: businessId,
          runId,
          acceptedFields,
        });
        const refreshed = await refetchBusiness();
        const persisted = refreshed.data?.business;
        const persistedValues = persisted as unknown as Record<string, unknown> | undefined;
        const suggestedValues = suggestion as unknown as Record<string, unknown>;
        const missingFields = acceptedFields.filter(
          (field) =>
            !persistedValues ||
            JSON.stringify(persistedValues[field]) !== JSON.stringify(suggestedValues[field]),
        );
        if (refreshed.error || !persisted || missingFields.length > 0) {
          throw new Error(
            `Business apply persistence verification failed: ${missingFields.join(',')}`,
          );
        }
        return { business: persisted, applied: result.applied };
      },
      {
        success: { title: t('detail.savedToast') },
        onSuccess: (result) => {
          onApplied?.(result.business, result.applied);
          onClose();
        },
        error: { title: t('ai.errorToast') },
      },
    );
  };

  const onRetryFailure = (): void => {
    if (failureReason === 'timeout' && runId) {
      setFailureReason(null);
      setRunStartedAt(Date.now());
      setElapsed(0);
      setPhase('progress');
      void refetchBusiness();
      return;
    }
    setRunId(null);
    setRunStartedAt(null);
    setFailureReason(null);
    void onSubmit();
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onClose() : undefined)}>
      <DialogContent size="md" data-testid="business-scan-modal">
        <DialogHeader
          title={t('ai.scanModalTitle')}
          description={t('ai.scanModalSubtitle')}
          onClose={onClose}
        />

        <DialogBody>
          {phase === 'input' ? (
            <div className="flex flex-col gap-3" data-testid="business-scan-input">
              <FieldAssembly label={t('ai.scanWebsiteLabel')} hint={t('ai.scanWebsiteHint')}>
                <Input
                  type="url"
                  value={website}
                  onChange={(event) => setWebsite(event.target.value)}
                  placeholder="https://"
                  data-testid="business-scan-website"
                />
              </FieldAssembly>
              <FieldAssembly label={t('ai.scanCompanyLabel')}>
                <Input
                  type="url"
                  value={linkedinCompany}
                  onChange={(event) => setLinkedinCompany(event.target.value)}
                  placeholder="https://linkedin.com/company/"
                  data-testid="business-scan-company"
                />
              </FieldAssembly>
              <FieldAssembly label={t('ai.scanProfileLabel')}>
                <Input
                  type="url"
                  value={linkedinProfile}
                  onChange={(event) => setLinkedinProfile(event.target.value)}
                  placeholder="https://linkedin.com/in/"
                  data-testid="business-scan-profile"
                />
              </FieldAssembly>
              <FieldAssembly label={t('ai.scanContextLabel')}>
                <Textarea
                  value={additionalContext}
                  onChange={(event) => setAdditionalContext(event.target.value)}
                  placeholder={t('ai.scanContextPlaceholder')}
                  rows={3}
                  data-testid="business-scan-context"
                />
              </FieldAssembly>
              <label className="flex items-center gap-2 text-sm text-secondary">
                <Checkbox
                  checked={readHistory}
                  onCheckedChange={(value) => setReadHistory(value === true)}
                  data-testid="business-scan-history"
                />
                {t('ai.scanReadHistoryLabel')}
              </label>
              {!hasInput ? (
                <p className="text-xs text-tertiary">{t('ai.scanRequiresInput')}</p>
              ) : null}
            </div>
          ) : null}

          {phase === 'progress' ? (
            <div className="flex flex-col gap-3" data-testid="business-scan-progress">
              <div className="flex items-center gap-2 text-sm">
                <Spinner size="sm" />
                <strong className="text-primary">{t('ai.progressTitle')}</strong>
                <span className="text-tertiary">
                  {t('ai.progressElapsed', { seconds: elapsed })}
                </span>
              </div>
              <ul className="flex flex-col gap-1 text-sm">
                {steps.map((step) => (
                  <li
                    key={step.id}
                    className="flex items-center gap-2"
                    data-testid={`business-scan-step-${step.id}`}
                    data-active={step.active || undefined}
                    data-done={step.done || undefined}
                  >
                    <span aria-hidden="true">{step.done ? '✓' : step.active ? '•' : '○'}</span>
                    <span>{t(`ai.progressStep.${step.id}`)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {phase === 'review-loading' ? (
            <div className="flex flex-col gap-3" data-testid="business-scan-review">
              <strong className="text-primary">{t('ai.reviewTitle')}</strong>
              {businessQuery.isError && !businessQuery.isFetching ? (
                <Banner tone="error" data-testid="business-scan-review-error">
                  <div className="flex items-center justify-between gap-3">
                    <span>{tErrors('ai.unavailable')}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => void businessQuery.refetch()}
                      data-testid="business-scan-review-retry"
                    >
                      {tCommon('actions.retry')}
                    </Button>
                  </div>
                </Banner>
              ) : (
                <div
                  className="flex items-center gap-2 text-sm text-secondary"
                  data-testid="business-scan-review-loading"
                >
                  <Spinner size="sm" />
                  <span>{tCommon('states.loading')}</span>
                </div>
              )}
            </div>
          ) : null}

          {phase === 'review' ? (
            <div className="flex flex-col gap-3" data-testid="business-scan-review">
              <div className="flex items-center justify-between gap-2">
                <strong className="text-primary">{t('ai.reviewTitle')}</strong>
                <span className="text-xs text-tertiary" data-testid="business-scan-review-count">
                  {t('ai.reviewSelected', {
                    count: acceptedFields.length,
                    total: reviewableFields.length,
                  })}
                </span>
              </div>
              <p className="text-xs text-tertiary">{t('ai.reviewSubtitle')}</p>
              {!suggestion || reviewableFields.length === 0 ? (
                <Banner tone="warning" data-testid="business-scan-review-empty">
                  {t('ai.reviewEmpty')}
                </Banner>
              ) : (
                <ul className="flex flex-col gap-2">
                  {reviewableFields.map((field) => {
                    const localCurrent = currentValues as Record<string, unknown> | undefined;
                    const currentRaw =
                      localCurrent && field in localCurrent
                        ? localCurrent[field]
                        : business && field in business
                          ? (business as unknown as Record<string, unknown>)[field]
                          : null;
                    const currentPreview = previewValue(currentRaw);
                    const suggestedPreview = previewValue(suggestion[field]);
                    const willReplace = fieldHasValue(currentRaw);
                    const isDiffOpen = diffOpen === field;
                    return (
                      <li
                        key={field}
                        className="rounded-lg border border-default bg-bg-elevated p-3"
                        data-testid={`business-scan-review-${field}`}
                      >
                        <div className="flex items-start gap-2">
                          <Checkbox
                            checked={accepted[field]}
                            onCheckedChange={(v) =>
                              setAccepted((prev) => ({ ...prev, [field]: v === true }))
                            }
                            aria-label={t(`ai.reviewField.${field}`)}
                            data-testid={`business-scan-review-${field}-check`}
                          />
                          <div className="flex flex-1 flex-col gap-1">
                            <div className="flex items-center justify-between gap-2">
                              <strong className="text-sm text-primary">
                                {t(`ai.reviewField.${field}`)}
                              </strong>
                              {willReplace ? (
                                <Pill tone="warning" variant="soft" size="sm">
                                  {t('ai.reviewWillReplace')}
                                </Pill>
                              ) : null}
                            </div>
                            <p className="text-xs italic text-tertiary">
                              {t(`ai.reviewFieldHint.${field}`)}
                            </p>
                            <p
                              className="text-xs text-secondary"
                              data-testid={`business-scan-review-${field}-suggested`}
                            >
                              <span className="font-medium">{t('ai.reviewSuggested')}:</span>{' '}
                              {suggestedPreview || t('ai.reviewEmptyValue')}
                            </p>
                            {willReplace ? (
                              <p className="text-xs text-tertiary">
                                <span className="font-medium">{t('ai.reviewCurrent')}:</span>{' '}
                                {currentPreview || t('ai.reviewEmptyValue')}
                              </p>
                            ) : null}
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setDiffOpen(isDiffOpen ? null : field)}
                              data-testid={`business-scan-review-${field}-diff`}
                            >
                              {isDiffOpen ? t('ai.reviewDiffHide') : t('ai.reviewDiff')}
                            </Button>
                            {isDiffOpen ? (
                              <div
                                className="mt-1 grid grid-cols-2 gap-2 rounded border border-default bg-bg-surface p-2 text-xs"
                                data-testid={`business-scan-review-${field}-diff-panel`}
                              >
                                <div className="flex flex-col gap-1">
                                  <span className="font-medium text-tertiary">
                                    {t('ai.reviewCurrent')}
                                  </span>
                                  <pre className="whitespace-pre-wrap text-secondary">
                                    {previewValue(currentRaw, 1000) || t('ai.reviewEmptyValue')}
                                  </pre>
                                </div>
                                <div className="flex flex-col gap-1">
                                  <span className="font-medium text-tertiary">
                                    {t('ai.reviewSuggested')}
                                  </span>
                                  <pre className="whitespace-pre-wrap text-secondary">
                                    {previewValue(suggestion[field], 1000) ||
                                      t('ai.reviewEmptyValue')}
                                  </pre>
                                </div>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ) : null}

          {phase === 'error' ? (
            <Banner tone="error" data-testid="business-scan-error">
              <div className="flex items-center justify-between gap-3">
                <span>
                  {failureReason === 'timeout' ? tErrors('ai.timeout') : tErrors('ai.unavailable')}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  loading={scan.isPending}
                  onClick={onRetryFailure}
                  data-testid="business-scan-retry"
                >
                  {tCommon('actions.retry')}
                </Button>
              </div>
            </Banner>
          ) : null}
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="ghost" size="md" onClick={onClose}>
            {t('ai.scanCancel')}
          </Button>
          {phase === 'input' ? (
            <Button
              type="button"
              variant="primary"
              size="md"
              disabled={!hasInput}
              loading={scan.isPending}
              onClick={() => void onSubmit()}
              data-testid="business-scan-submit"
            >
              {t('ai.scanSubmit')}
            </Button>
          ) : null}
          {phase === 'review' ? (
            <Button
              type="button"
              variant="primary"
              size="md"
              disabled={acceptedFields.length === 0}
              loading={apply.isPending}
              onClick={() => void onApply()}
              data-testid="business-scan-apply"
            >
              {t('ai.reviewApply', { count: acceptedFields.length })}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
